/**
 * @module textStats
 * @description Text statistics utilities for Markdown content
 *
 * Provides word and character counting functions that properly handle
 * Markdown syntax by stripping formatting before counting.
 *
 * @example
 * import { countWords, countCharacters, getTextStats } from '@utils/textStats';
 *
 * const words = countWords('# Hello World');  // 2
 * const chars = countCharacters('Hello');     // 5
 * const stats = getTextStats(markdownContent); // { words: 150, characters: 823 }
 */

/**
 * Text statistics result
 */
export interface TextStats {
  words: number;
  characters: number;
}

/**
 * Remove Markdown formatting from text for accurate counting
 */
function stripMarkdown(text: string): string {
  if (!text) return "";

  return text
    .replace(/```[\s\S]*?```/g, "") // Code blocks
    .replace(/`[^`]+`/g, "") // Inline code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Links: [text](url) -> text
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "") // Images: ![alt](url) -> removed
    .replace(/^#{1,6}\s+/gm, "") // Headings: # Title -> Title
    .replace(/[*_~`]/g, "") // Bold, italic, strikethrough markers
    .replace(/^\s*[-*+]\s+/gm, "") // Unordered list markers
    .replace(/^\s*\d+\.\s+/gm, "") // Ordered list markers
    .replace(/^\s*>\s+/gm, "") // Blockquotes
    .replace(/\n{3,}/g, "\n\n") // Multiple newlines -> double
    .trim();
}

/**
 * Count words in Markdown content
 *
 * Strips Markdown formatting before counting to provide accurate
 * word counts that reflect the actual readable content.
 *
 * @example
 * countWords('# Hello World\n\nThis is **bold** text.')
 * // Returns: 6 (Hello, World, This, is, bold, text)
 */
export function countWords(text: string): number {
  const cleanText = stripMarkdown(text);
  if (!cleanText) return 0;

  return cleanText.split(/\s+/).filter((word) => word.length > 0).length;
}

/**
 * Count characters in Markdown content
 *
 * Strips Markdown formatting before counting to provide accurate
 * character counts that reflect the actual readable content.
 *
 * @example
 * countCharacters('**Hello**')
 * // Returns: 5 (just "Hello", not the asterisks)
 */
export function countCharacters(text: string): number {
  const cleanText = stripMarkdown(text);
  return cleanText.length;
}

/**
 * Get complete text statistics for Markdown content
 *
 * @example
 * const stats = getTextStats('# My Document\n\nSome content here.');
 * // Returns: { words: 5, characters: 26 }
 */
export function getTextStats(text: string): TextStats {
  const cleanText = stripMarkdown(text);

  return {
    words: cleanText
      ? cleanText.split(/\s+/).filter((w) => w.length > 0).length
      : 0,
    characters: cleanText.length,
  };
}

/**
 * Estimate reading time based on word count
 *
 * @param text - Markdown text
 * @param wordsPerMinute - Reading speed (default: 200)
 * @returns Estimated reading time in minutes
 *
 * @example
 * estimateReadingTime('...long text...', 200)
 * // Returns: 5 (minutes)
 */
export function estimateReadingTime(
  text: string,
  wordsPerMinute: number = 200,
): number {
  const words = countWords(text);
  return Math.ceil(words / wordsPerMinute);
}
